
import { YM2612Patch, FMAlgorithm, OperatorParams } from '../types';

export interface DMPLoadResult {
  patch: YM2612Patch;
  logs: string[];
}

// Specs for DMP (DefleMask Preset Format v1.0.0+)
// Header:
// Byte 0: Version (11)
// Byte 1: System (Genesis = 0x02)
// Byte 2: Mode (FM = 0x01)
// Byte 3: LFO (FMS)
// Byte 4: FB
// Byte 5: ALG
// Byte 6: LFO2 (AMS)
// Byte 7+: Operator Data (4 * 11 bytes)

export const parseDMP = (buffer: ArrayBuffer, filename: string): DMPLoadResult => {
    const view = new DataView(buffer);
    const logs: string[] = [];
    
    logs.push(`[INIT] Parsing ${filename} (${buffer.byteLength} bytes)`);

    // 1. Validate Size
    // Min size = 7 (Header) + 4 * 11 (Ops) = 51 bytes
    if (buffer.byteLength < 51) {
        throw new Error("File too small. Expected at least 51 bytes.");
    }

    // 2. Version Check
    const version = view.getUint8(0);
    logs.push(`[HEADER] Version: ${version} (Expected: 11)`);
    if (version !== 11) {
        logs.push(`[WARN] Version mismatch. Parsing may fail if format differs significantly.`);
    }

    // 3. System Check
    const system = view.getUint8(1);
    logs.push(`[HEADER] System: 0x${system.toString(16).toUpperCase().padStart(2, '0')} (Genesis=0x02, Legacy=0x01)`);
    // Allow 0x01 (Old Genesis ID) and 0x02 (New Genesis ID)
    if (system !== 0x02 && system !== 0x01) {
         throw new Error(`Invalid System ID. Expected 0x02, got 0x${system.toString(16)}`);
    }

    // 4. Mode Check
    const mode = view.getUint8(2);
    logs.push(`[HEADER] Mode: ${mode} (0=STD, 1=FM)`);
    if (mode !== 0x01) {
        throw new Error(`Invalid Instrument Mode. This parser only supports FM (1). Got ${mode}`);
    }

    // 5. Global Parameters
    const fms = view.getUint8(3);
    const fb = view.getUint8(4);
    const alg = view.getUint8(5);
    const ams = view.getUint8(6); // LFO2 in spec

    logs.push(`[GLOBAL] ALG: ${alg} | FB: ${fb} | FMS: ${fms} | AMS: ${ams}`);

    // 6. Operators
    // 4 Operators, 11 bytes each
    // Structure: MULT, TL, AR, DR, SL, RR, AM, RS, DT, D2R, SSGEG
    const operators: OperatorParams[] = [];
    let offset = 7;

    for (let i = 0; i < 4; i++) {
        if (offset + 11 > buffer.byteLength) {
             throw new Error(`Unexpected EOF reading Operator ${i+1}`);
        }

        const mul = view.getUint8(offset + 0);
        const tl = view.getUint8(offset + 1);
        const ar = view.getUint8(offset + 2);
        const dr = view.getUint8(offset + 3);
        const sl = view.getUint8(offset + 4);
        const rr = view.getUint8(offset + 5);
        const am = view.getUint8(offset + 6);
        const rs = view.getUint8(offset + 7);
        const dt = view.getUint8(offset + 8);
        const d2r = view.getUint8(offset + 9); // Secondary Decay
        const ssgeg = view.getUint8(offset + 10); // Ignored in basic engine

        operators.push({
            id: i,
            mul,
            tl,
            ar,
            dr,
            sl,
            rr,
            ams: am === 1, // Assuming 1 = Enabled
            rs,
            dt,
            sr: d2r
        });

        logs.push(`[OP ${i+1}] AR:${ar} DR:${dr} SL:${sl} RR:${rr} TL:${tl} MUL:${mul} DT:${dt} D2R:${d2r}`);
        
        offset += 11;
    }

    const patch: YM2612Patch = {
        name: filename.replace(/\.[^/.]+$/, "").toUpperCase(),
        description: "Standard DMP Preset", // Default description
        algorithm: alg as FMAlgorithm,
        feedback: fb,
        lfoFreq: 0, // Defaulting as missing in DMP basic parse
        ams,
        fms,
        operators: [operators[0], operators[1], operators[2], operators[3]]
    };

    logs.push(`[SUCCESS] Patch "${patch.name}" ready.`);

    return {
        patch,
        logs
    };
};

export const writeDMP = (patch: YM2612Patch): Uint8Array => {
    // Header (7 bytes) + 4 Ops * 11 bytes = 51 bytes
    const buffer = new Uint8Array(51);
    
    // Header
    buffer[0] = 11; // Version
    buffer[1] = 0x02; // System (Genesis)
    buffer[2] = 0x01; // Mode (FM)
    buffer[3] = patch.fms & 0xFF;
    buffer[4] = patch.feedback & 0xFF;
    buffer[5] = patch.algorithm & 0xFF;
    buffer[6] = patch.ams & 0xFF;

    let offset = 7;
    // Operators
    // DMP stores Ops 1, 3, 2, 4 usually but standard linear read 0-3 in parseDMP works.
    // We will write them back in index order 0, 1, 2, 3 which corresponds to read order.
    for (let i = 0; i < 4; i++) {
        const op = patch.operators[i];
        
        buffer[offset + 0] = op.mul & 0xFF;
        buffer[offset + 1] = op.tl & 0xFF;
        buffer[offset + 2] = op.ar & 0xFF;
        buffer[offset + 3] = op.dr & 0xFF;
        buffer[offset + 4] = op.sl & 0xFF;
        buffer[offset + 5] = op.rr & 0xFF;
        buffer[offset + 6] = op.ams ? 1 : 0;
        buffer[offset + 7] = op.rs & 0xFF;
        buffer[offset + 8] = op.dt & 0xFF;
        buffer[offset + 9] = op.sr & 0xFF; // Mapped sr to d2r in parse
        buffer[offset + 10] = 0; // SSGEG (not fully implemented in types, defaulting 0)

        offset += 11;
    }

    return buffer;
};

// Raw Byte Arrays from Kasser Synths / DefleMask
const RAW_ELECTRO_BASS = [
  0x0B, 0x02, 0x01, 0x00, 0x05, 0x01, 0x00, 0x00, 0x16, 0x1F, 0x09, 0x05,
  0x0F, 0x00, 0x00, 0x03, 0x0C, 0x00, 0x00, 0x17, 0x1F, 0x08, 0x0F, 0x0F,
  0x00, 0x00, 0x03, 0x00, 0x00, 0x00, 0x16, 0x1F, 0x09, 0x05, 0x0F, 0x00,
  0x00, 0x03, 0x0C, 0x00, 0x00, 0x05, 0x1F, 0x00, 0x00, 0x0F, 0x00, 0x00,
  0x03, 0x00, 0x00
];

const RAW_PADS = [
  0x0B, 0x02, 0x01, 0x04, 0x00, 0x04, 0x00, 0x01, 0x11, 0x1F, 0x00, 0x00,
  0x05, 0x00, 0x00, 0x06, 0x00, 0x00, 0x01, 0x11, 0x1F, 0x00, 0x00, 0x05,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x02, 0x0B, 0x0B, 0x00, 0x00, 0x04, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x02, 0x0B, 0x0B, 0x00, 0x00, 0x04, 0x00, 0x00,
  0x06, 0x00, 0x00
];

const RAW_DX_PIANO = [
  0x0B, 0x02, 0x01, 0x00, 0x00, 0x01, 0x00, 0x09, 0x3C, 0x1F, 0x09, 0x0F,
  0x05, 0x00, 0x02, 0x06, 0x00, 0x00, 0x00, 0x1C, 0x1F, 0x07, 0x0F, 0x05,
  0x00, 0x02, 0x03, 0x00, 0x00, 0x09, 0x3C, 0x1F, 0x09, 0x0F, 0x05, 0x00,
  0x02, 0x00, 0x00, 0x00, 0x00, 0x0A, 0x1F, 0x03, 0x0F, 0x05, 0x00, 0x02,
  0x03, 0x00, 0x00
];

const RAW_SYNTH_BRASS = [
  0x0B, 0x02, 0x01, 0x02, 0x06, 0x02, 0x00, 0x01, 0x14, 0x14, 0x06, 0x0F,
  0x0F, 0x00, 0x00, 0x03, 0x00, 0x00, 0x01, 0x2A, 0x1F, 0x00, 0x00, 0x0F,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x01, 0x1C, 0x1F, 0x00, 0x00, 0x0F, 0x00,
  0x00, 0x06, 0x00, 0x00, 0x01, 0x0C, 0x1F, 0x00, 0x00, 0x0F, 0x00, 0x00,
  0x03, 0x00, 0x00
];

const RAW_FM_SNARE = [
  0x0B, 0x02, 0x01, 0x00, 0x07, 0x04, 0x00, 0x0F, 0x00, 0x1F, 0x00, 0x00,
  0x04, 0x00, 0x00, 0x00, 0x00, 0x00, 0x01, 0x00, 0x1F, 0x19, 0x0F, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x01, 0x03, 0x1F, 0x0F, 0x06, 0x06, 0x00,
  0x00, 0x00, 0x0E, 0x00, 0x01, 0x03, 0x1F, 0x10, 0x0F, 0x0A, 0x00, 0x00,
  0x00, 0x0F, 0x00
];

const RAW_FM_KICK = [
  0x0B, 0x02, 0x01, 0x00, 0x07, 0x04, 0x00, 0x01, 0x00, 0x1F, 0x19, 0x0A,
  0x0F, 0x00, 0x00, 0x00, 0x19, 0x00, 0x01, 0x08, 0x1F, 0x18, 0x0A, 0x0F,
  0x00, 0x00, 0x00, 0x17, 0x00, 0x00, 0x01, 0x1F, 0x13, 0x0A, 0x0F, 0x00,
  0x00, 0x00, 0x12, 0x00, 0x00, 0x01, 0x1F, 0x12, 0x0A, 0x0F, 0x00, 0x00,
  0x00, 0x11, 0x00
];

export const createPatchFromBytes = (name: string, description: string, bytes: number[]): YM2612Patch => {
    const buffer = new Uint8Array(bytes).buffer;
    const { patch } = parseDMP(buffer, name);
    patch.name = name; // Ensure exact name
    patch.description = description;
    return patch;
};

export const PRESETS: Record<string, YM2612Patch> = {
    ELECTRO_BASS: createPatchFromBytes("THUNDER SLAP", "A gritty, punchy bassline reminiscent of Level 1 street brawlers.", RAW_ELECTRO_BASS),
    PADS: createPatchFromBytes("STAR GAZER", "Ethereal sweeping pad with slow attack for sci-fi intros.", RAW_PADS),
    DX_PIANO: createPatchFromBytes("GLASS KEYS", "Classic 80s digital ballad electric piano with bright harmonics.", RAW_DX_PIANO),
    SYNTH_BRASS: createPatchFromBytes("HERO BRASS", "Bright, triumphant leads perfect for high-energy action sequences.", RAW_SYNTH_BRASS),
    FM_SNARE: createPatchFromBytes("CYBER SNAP", "Tight, noise-based percussive hit with a metallic tail.", RAW_FM_SNARE),
    FM_KICK: createPatchFromBytes("TITAN THUD", "Low-frequency impact designed for driving rhythms.", RAW_FM_KICK)
};

export const LIBRARY_PRESETS = [
    { 
      name: "Sonic 1 - Green Hill Bass", 
      description: "Iconic driving FM bass from the green hills.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x04, 0x00, 0x00, 0x06, 0x19, 0x1F, 0x07, 0x02, 0x00, 0x00, 0x03, 0x06, 0x07, 0x00, 0x00, 0x13, 0x1F, 0x09, 0x01, 0x00, 0x00, 0x02, 0x06, 0x06, 0x00, 0x05, 0x37, 0x1F, 0x06, 0x01, 0x00, 0x00, 0x03, 0x06, 0x06, 0x00, 0x01, 0x0B, 0x1F, 0x06, 0x0F, 0x08, 0x00, 0x02, 0x06, 0x08, 0x00]
    },
    { 
      name: "Sonic 1 - Green Hill Piano", 
      description: "Bright, chord-stabbing electronic piano.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x00, 0x04, 0x00, 0x02, 0x23, 0x12, 0x00, 0x00, 0x0F, 0x00, 0x00, 0x00, 0x00, 0x00, 0x02, 0x23, 0x12, 0x00, 0x00, 0x0F, 0x00, 0x00, 0x06, 0x00, 0x00, 0x02, 0x0A, 0x12, 0x08, 0x01, 0x0F, 0x00, 0x00, 0x03, 0x08, 0x00, 0x02, 0x0A, 0x12, 0x08, 0x01, 0x0F, 0x00, 0x00, 0x06, 0x08, 0x00]
    },
    { 
      name: "Sonic 2 - Chemical Plant Horn", 
      description: "Urgent, metallic horn lead for fast zones.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x07, 0x02, 0x00, 0x01, 0x17, 0x0E, 0x0E, 0x01, 0x0F, 0x00, 0x02, 0x03, 0x00, 0x00, 0x01, 0x27, 0x0D, 0x0E, 0x01, 0x0F, 0x00, 0x02, 0x03, 0x00, 0x00, 0x07, 0x28, 0x0E, 0x0E, 0x0F, 0x0F, 0x00, 0x02, 0x03, 0x00, 0x00, 0x01, 0x05, 0x13, 0x03, 0x00, 0x0F, 0x00, 0x01, 0x03, 0x00, 0x00]
    },
    { 
      name: "Sonic 2 - Chemical Plant Guitar", 
      description: "Funky, synthesized guitar lick.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x07, 0x03, 0x00, 0x0F, 0x22, 0x1F, 0x0C, 0x0F, 0x03, 0x00, 0x03, 0x03, 0x0F, 0x00, 0x01, 0x22, 0x1F, 0x0A, 0x05, 0x05, 0x00, 0x00, 0x03, 0x00, 0x00, 0x06, 0x20, 0x1F, 0x00, 0x00, 0x05, 0x00, 0x00, 0x03, 0x00, 0x00, 0x02, 0x10, 0x1F, 0x03, 0x05, 0x0C, 0x00, 0x03, 0x03, 0x01, 0x00]
    },
    { 
      name: "Sonic 3 - Ice Cap Percussion", 
      description: "Hollow, resonant metallic percussion.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x00, 0x06, 0x00, 0x04, 0x1D, 0x1F, 0x0F, 0x01, 0x0F, 0x00, 0x00, 0x03, 0x0D, 0x00, 0x03, 0x1E, 0x1F, 0x0F, 0x01, 0x0F, 0x00, 0x00, 0x03, 0x11, 0x00, 0x04, 0x0C, 0x1F, 0x0F, 0x00, 0x0F, 0x00, 0x00, 0x03, 0x12, 0x00, 0x04, 0x08, 0x1F, 0x0F, 0x00, 0x0F, 0x00, 0x00, 0x06, 0x10, 0x00]
    },
    { 
      name: "Sonic 3 - Ice Cap Bass", 
      description: "Deep, cold sub-bass with a punch.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x04, 0x00, 0x00, 0x04, 0x22, 0x1F, 0x07, 0x02, 0x00, 0x00, 0x03, 0x06, 0x07, 0x00, 0x00, 0x14, 0x1F, 0x08, 0x01, 0x0F, 0x00, 0x02, 0x06, 0x0A, 0x00, 0x08, 0x37, 0x1F, 0x08, 0x01, 0x0F, 0x00, 0x03, 0x06, 0x0E, 0x00, 0x01, 0x02, 0x1F, 0x0A, 0x01, 0x0F, 0x00, 0x02, 0x06, 0x11, 0x00]
    },
    { 
      name: "Sonic 3 - Ice Cap Synth", 
      description: "Crystalline, icy synth lead.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x06, 0x05, 0x00, 0x02, 0x1D, 0x10, 0x07, 0x02, 0x08, 0x00, 0x00, 0x03, 0x01, 0x00, 0x02, 0x15, 0x0C, 0x04, 0x01, 0x0B, 0x00, 0x00, 0x03, 0x0A, 0x00, 0x04, 0x16, 0x0A, 0x04, 0x01, 0x0B, 0x00, 0x00, 0x03, 0x0A, 0x00, 0x01, 0x16, 0x0E, 0x04, 0x01, 0x0B, 0x00, 0x00, 0x03, 0x0A, 0x00]
    },
    { 
      name: "SoR - Keep Groovin Piano", 
      description: "House-style rave piano chords.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x07, 0x02, 0x00, 0x01, 0x25, 0x1C, 0x04, 0x01, 0x05, 0x01, 0x01, 0x00, 0x00, 0x00, 0x03, 0x25, 0x1D, 0x04, 0x01, 0x06, 0x01, 0x01, 0x06, 0x03, 0x00, 0x0C, 0x2F, 0x16, 0x09, 0x01, 0x02, 0x01, 0x01, 0x03, 0x01, 0x00, 0x01, 0x11, 0x1F, 0x0C, 0x02, 0x08, 0x00, 0x01, 0x03, 0x0D, 0x00]
    },
    { 
      name: "SoR - Keep Groovin Brass", 
      description: "Punchy, synthesized brass section.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x07, 0x04, 0x00, 0x02, 0x1A, 0x13, 0x0D, 0x01, 0x07, 0x01, 0x01, 0x03, 0x00, 0x00, 0x01, 0x1B, 0x13, 0x0D, 0x02, 0x07, 0x01, 0x01, 0x06, 0x00, 0x00, 0x02, 0x11, 0x10, 0x08, 0x01, 0x08, 0x01, 0x01, 0x06, 0x00, 0x00, 0x02, 0x11, 0x10, 0x08, 0x01, 0x07, 0x01, 0x01, 0x03, 0x00, 0x00]
    },
    { 
      name: "SoR 2 - Go Straight Bass", 
      description: "Aggressive, pumping dance bassline.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x00, 0x06, 0x00, 0x00, 0x17, 0x1F, 0x0A, 0x0F, 0x08, 0x01, 0x02, 0x03, 0x0E, 0x00, 0x01, 0x08, 0x1F, 0x11, 0x0F, 0x08, 0x01, 0x02, 0x03, 0x0E, 0x00, 0x00, 0x08, 0x1F, 0x0A, 0x04, 0x08, 0x01, 0x00, 0x03, 0x0A, 0x00, 0x02, 0x08, 0x1F, 0x14, 0x0F, 0x08, 0x01, 0x00, 0x03, 0x0E, 0x00]
    },
    { 
      name: "SoR 2 - Go Straight Organ", 
      description: "Percussive, deep house organ.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x07, 0x02, 0x00, 0x01, 0x25, 0x14, 0x04, 0x01, 0x05, 0x01, 0x01, 0x00, 0x00, 0x00, 0x03, 0x25, 0x13, 0x04, 0x01, 0x05, 0x01, 0x01, 0x06, 0x03, 0x00, 0x0C, 0x2F, 0x14, 0x09, 0x01, 0x05, 0x01, 0x01, 0x03, 0x01, 0x00, 0x01, 0x10, 0x11, 0x0A, 0x02, 0x05, 0x01, 0x01, 0x03, 0x05, 0x00]
    },
    { 
      name: "SoR 2 - Go Straight Horn", 
      description: "Sharp, piercing synth brass lead.",
      data: [0x0B, 0x02, 0x01, 0x04, 0x05, 0x03, 0x00, 0x01, 0x08, 0x1F, 0x00, 0x0A, 0x0A, 0x01, 0x00, 0x06, 0x01, 0x00, 0x05, 0x50, 0x1E, 0x00, 0x0F, 0x09, 0x01, 0x02, 0x06, 0x12, 0x00, 0x02, 0x12, 0x19, 0x00, 0x0A, 0x0F, 0x01, 0x01, 0x06, 0x17, 0x00, 0x01, 0x18, 0x1E, 0x05, 0x0F, 0x0C, 0x01, 0x01, 0x06, 0x0A, 0x00]
    },
    { 
      name: "SoR 2 - Dreamer Bass", 
      description: "Smooth, rolling deep bass.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x00, 0x06, 0x00, 0x00, 0x17, 0x1F, 0x0A, 0x0F, 0x08, 0x01, 0x02, 0x00, 0x0E, 0x00, 0x00, 0x0A, 0x1F, 0x11, 0x0F, 0x08, 0x01, 0x02, 0x06, 0x0E, 0x00, 0x00, 0x0A, 0x1F, 0x04, 0x04, 0x08, 0x01, 0x00, 0x00, 0x04, 0x00, 0x00, 0x0A, 0x1F, 0x04, 0x0F, 0x08, 0x01, 0x00, 0x06, 0x04, 0x00]
    },
    { 
      name: "SoR 2 - Dreamer HiHat", 
      description: "Crisp, white-noise driven hi-hat.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x07, 0x04, 0x00, 0x02, 0x00, 0x1F, 0x00, 0x03, 0x0F, 0x01, 0x00, 0x06, 0x00, 0x00, 0x03, 0x00, 0x1F, 0x00, 0x02, 0x0F, 0x01, 0x00, 0x00, 0x1F, 0x00, 0x02, 0x10, 0x1F, 0x11, 0x02, 0x0F, 0x01, 0x00, 0x06, 0x12, 0x00, 0x03, 0x10, 0x1F, 0x11, 0x04, 0x0F, 0x01, 0x00, 0x00, 0x12, 0x00]
    },
    { 
      name: "SoR 2 - Dreamer Piano", 
      description: "Melancholic, mellow electric piano.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x07, 0x02, 0x00, 0x01, 0x25, 0x13, 0x04, 0x01, 0x07, 0x01, 0x01, 0x00, 0x00, 0x00, 0x03, 0x25, 0x12, 0x04, 0x01, 0x07, 0x01, 0x01, 0x06, 0x03, 0x00, 0x0C, 0x2F, 0x13, 0x09, 0x01, 0x07, 0x01, 0x01, 0x03, 0x01, 0x00, 0x01, 0x12, 0x12, 0x0A, 0x05, 0x07, 0x01, 0x01, 0x03, 0x04, 0x00]
    },
    { 
      name: "SoR 2 - Dreamer Distorted Piano", 
      description: "Gritty, overdriven keys.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x07, 0x02, 0x00, 0x01, 0x1D, 0x19, 0x0A, 0x01, 0x05, 0x01, 0x01, 0x03, 0x00, 0x00, 0x01, 0x30, 0x1C, 0x0D, 0x02, 0x07, 0x01, 0x01, 0x06, 0x00, 0x00, 0x05, 0x0F, 0x19, 0x0B, 0x05, 0x08, 0x01, 0x01, 0x03, 0x00, 0x00, 0x01, 0x12, 0x0E, 0x04, 0x00, 0x06, 0x01, 0x01, 0x03, 0x00, 0x00]
    },
    { 
      name: "TJ&E - Funkotron Bass", 
      description: "Slap-style funk bass with character.",
      data: [0x0B, 0x02, 0x01, 0x03, 0x05, 0x05, 0x01, 0x00, 0x0F, 0x1F, 0x0C, 0x07, 0x08, 0x01, 0x02, 0x01, 0x1F, 0x00, 0x00, 0x0E, 0x1F, 0x00, 0x0B, 0x0F, 0x00, 0x03, 0x06, 0x1F, 0x00, 0x00, 0x1D, 0x1F, 0x02, 0x03, 0x0C, 0x01, 0x00, 0x02, 0x12, 0x00, 0x00, 0x11, 0x1F, 0x09, 0x0F, 0x0F, 0x00, 0x01, 0x03, 0x00, 0x00]
    },
    { 
      name: "TJ&E - Pluck Bass", 
      description: "Tight, plucked synthetic bass.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x00, 0x02, 0x00, 0x00, 0x1C, 0x13, 0x05, 0x02, 0x09, 0x00, 0x03, 0x03, 0x00, 0x00, 0x01, 0x27, 0x1F, 0x06, 0x05, 0x00, 0x00, 0x03, 0x03, 0x12, 0x00, 0x03, 0x13, 0x1F, 0x0A, 0x08, 0x0F, 0x00, 0x03, 0x03, 0x00, 0x00, 0x00, 0x0C, 0x1F, 0x07, 0x00, 0x09, 0x00, 0x02, 0x03, 0x00, 0x00]
    },
    { 
      name: "TJ&E - Overdrive Guitar", 
      description: "Crunchy, distorted lead guitar.",
      data: [0x0B, 0x02, 0x01, 0x04, 0x05, 0x03, 0x00, 0x0F, 0x2C, 0x1A, 0x0E, 0x05, 0x0F, 0x00, 0x00, 0x06, 0x0E, 0x00, 0x00, 0x12, 0x1C, 0x12, 0x03, 0x0C, 0x00, 0x01, 0x00, 0x06, 0x00, 0x00, 0x0D, 0x18, 0x0F, 0x01, 0x0F, 0x00, 0x00, 0x00, 0x04, 0x00, 0x01, 0x0C, 0x1F, 0x15, 0x01, 0x0A, 0x00, 0x01, 0x06, 0x09, 0x00]
    },
    { 
      name: "TJ&E - WOW Effect", 
      description: "Vocal-like formant filter effect.",
      data: [0x0B, 0x02, 0x01, 0x01, 0x00, 0x04, 0x00, 0x01, 0x0F, 0x0B, 0x0D, 0x06, 0x0A, 0x00, 0x01, 0x03, 0x09, 0x00, 0x01, 0x1B, 0x01, 0x13, 0x00, 0x0A, 0x00, 0x02, 0x03, 0x02, 0x00, 0x03, 0x09, 0x0D, 0x05, 0x04, 0x0A, 0x00, 0x00, 0x00, 0x00, 0x00, 0x01, 0x0D, 0x16, 0x07, 0x00, 0x0A, 0x00, 0x00, 0x01, 0x00, 0x00]
    },
    { 
      name: "Ecco - Opening Pad", 
      description: "Atmospheric, underwater ambient swelling pad.",
      data: [0x0B, 0x02, 0x01, 0x01, 0x05, 0x00, 0x00, 0x06, 0x04, 0x10, 0x1C, 0x06, 0x0F, 0x00, 0x03, 0x06, 0x0D, 0x00, 0x02, 0x1C, 0x08, 0x0A, 0x0F, 0x0B, 0x01, 0x01, 0x01, 0x17, 0x00, 0x02, 0x06, 0x0F, 0x0B, 0x04, 0x0B, 0x00, 0x03, 0x00, 0x0C, 0x00, 0x02, 0x0C, 0x16, 0x07, 0x0A, 0x0F, 0x01, 0x01, 0x05, 0x13, 0x00]
    },
    { 
      name: "Ecco - Opening Percussion", 
      description: "Echoing, sonar-like click percussion.",
      data: [0x0B, 0x02, 0x01, 0x05, 0x07, 0x04, 0x00, 0x03, 0x0B, 0x1F, 0x06, 0x0B, 0x05, 0x00, 0x03, 0x04, 0x11, 0x00, 0x00, 0x08, 0x1F, 0x08, 0x00, 0x08, 0x00, 0x03, 0x03, 0x14, 0x00, 0x00, 0x04, 0x1F, 0x0E, 0x0F, 0x08, 0x00, 0x02, 0x03, 0x1F, 0x00, 0x00, 0x00, 0x1F, 0x10, 0x06, 0x08, 0x00, 0x00, 0x03, 0x10, 0x00]
    },
    { 
      name: "Time Trax - Title Pad", 
      description: "Dark, cinematic sci-fi texture.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x05, 0x04, 0x00, 0x02, 0x0F, 0x05, 0x1F, 0x00, 0x09, 0x00, 0x00, 0x01, 0x08, 0x00, 0x03, 0x0E, 0x05, 0x1F, 0x00, 0x09, 0x00, 0x00, 0x06, 0x08, 0x00, 0x02, 0x12, 0x1F, 0x1F, 0x00, 0x09, 0x00, 0x00, 0x03, 0x00, 0x00, 0x03, 0x12, 0x1F, 0x1F, 0x00, 0x09, 0x00, 0x00, 0x03, 0x00, 0x00]
    },
    { 
      name: "Time Trax - Title Bass", 
      description: "Heavy, industrial throbbing bass.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x00, 0x03, 0x00, 0x01, 0x07, 0x1F, 0x1F, 0x00, 0x0F, 0x00, 0x00, 0x03, 0x11, 0x00, 0x00, 0x19, 0x1F, 0x1F, 0x00, 0x0F, 0x00, 0x00, 0x05, 0x07, 0x00, 0x00, 0x2D, 0x1F, 0x1F, 0x00, 0x09, 0x00, 0x00, 0x03, 0x00, 0x00, 0x00, 0x03, 0x1F, 0x1F, 0x00, 0x07, 0x00, 0x00, 0x03, 0x00, 0x00]
    },
    { 
      name: "Time Trax - Title Distorted Gtr", 
      description: "Searing, high-gain metal guitar.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x07, 0x02, 0x00, 0x08, 0x18, 0x1F, 0x1F, 0x00, 0x0F, 0x00, 0x00, 0x03, 0x00, 0x00, 0x04, 0x00, 0x1F, 0x19, 0x02, 0x0F, 0x00, 0x00, 0x05, 0x0A, 0x00, 0x04, 0x00, 0x1F, 0x15, 0x09, 0x0F, 0x00, 0x00, 0x03, 0x00, 0x00, 0x04, 0x09, 0x1F, 0x1F, 0x00, 0x0F, 0x00, 0x00, 0x03, 0x00, 0x00]
    },
    { 
      name: "Time Trax - Title TOM", 
      description: "Powerful, gated tom drum.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x07, 0x04, 0x00, 0x0F, 0x00, 0x1F, 0x00, 0x0F, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x0A, 0x14, 0x10, 0x07, 0x08, 0x00, 0x00, 0x00, 0x16, 0x00, 0x02, 0x7F, 0x1C, 0x19, 0x0F, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x02, 0x1F, 0x00, 0x09, 0x08, 0x00, 0x00, 0x00, 0x16, 0x00]
    },
    { 
      name: "Time Trax - Title Snare", 
      description: "Sharp, mechanical snare hit.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x07, 0x04, 0x00, 0x00, 0x00, 0x1F, 0x00, 0x0F, 0x00, 0x00, 0x00, 0x03, 0x00, 0x00, 0x03, 0x00, 0x1F, 0x19, 0x0F, 0x00, 0x00, 0x00, 0x03, 0x00, 0x00, 0x00, 0x05, 0x12, 0x11, 0x0A, 0x07, 0x00, 0x00, 0x03, 0x16, 0x00, 0x00, 0x02, 0x1F, 0x0C, 0x02, 0x06, 0x00, 0x00, 0x03, 0x16, 0x00]
    },
    { 
      name: "Time Trax - Title Hihat", 
      description: "Metallic, closed hi-hat shaker.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x07, 0x04, 0x00, 0x0F, 0x00, 0x1F, 0x00, 0x0F, 0x00, 0x00, 0x00, 0x03, 0x00, 0x00, 0x00, 0x7F, 0x1F, 0x1F, 0x0F, 0x00, 0x00, 0x00, 0x03, 0x00, 0x00, 0x06, 0x0A, 0x1F, 0x14, 0x0D, 0x00, 0x00, 0x00, 0x03, 0x1F, 0x00, 0x00, 0x7F, 0x1F, 0x1F, 0x0F, 0x00, 0x00, 0x00, 0x03, 0x00, 0x00]
    },
    { 
      name: "Thunderforce IV - Shred Guitar", 
      description: "Fast, shredding FM metal guitar.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x05, 0x00, 0x00, 0x03, 0x0E, 0x1F, 0x14, 0x01, 0x01, 0x00, 0x03, 0x06, 0x00, 0x00, 0x00, 0x12, 0x1F, 0x01, 0x01, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x03, 0x1B, 0x1C, 0x05, 0x02, 0x01, 0x00, 0x03, 0x02, 0x01, 0x00, 0x00, 0x15, 0x1F, 0x01, 0x0F, 0x08, 0x01, 0x00, 0x06, 0x1D, 0x00]
    },
    { 
      name: "Thunderforce IV - Power TOM", 
      description: "Thunderous, rolling tom fill.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x05, 0x04, 0x00, 0x00, 0x00, 0x18, 0x1A, 0x0F, 0x0F, 0x00, 0x00, 0x03, 0x1F, 0x00, 0x00, 0x0C, 0x1A, 0x17, 0x0F, 0x0F, 0x00, 0x00, 0x03, 0x1F, 0x00, 0x00, 0x0F, 0x1A, 0x00, 0x00, 0x07, 0x00, 0x00, 0x03, 0x00, 0x00, 0x00, 0x0F, 0x1A, 0x00, 0x00, 0x07, 0x00, 0x00, 0x03, 0x00, 0x00]
    },
    { 
      name: "Thunderforce IV - Metal Dist", 
      description: "Heavy metal rhythm distortion.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x04, 0x00, 0x00, 0x05, 0x19, 0x0F, 0x03, 0x06, 0x06, 0x00, 0x00, 0x03, 0x05, 0x00, 0x01, 0x19, 0x0F, 0x04, 0x02, 0x06, 0x00, 0x00, 0x03, 0x03, 0x00, 0x0B, 0x19, 0x13, 0x02, 0x06, 0x06, 0x00, 0x00, 0x04, 0x02, 0x00, 0x03, 0x10, 0x1B, 0x04, 0x02, 0x06, 0x00, 0x00, 0x03, 0x03, 0x00]
    },
    { 
      name: "Chakan - Intro Pad 1", 
      description: "Eerie, gothic swelling drone.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x00, 0x02, 0x00, 0x00, 0x12, 0x08, 0x13, 0x07, 0x05, 0x00, 0x02, 0x03, 0x1F, 0x00, 0x01, 0x04, 0x02, 0x06, 0x05, 0x05, 0x00, 0x02, 0x03, 0x1F, 0x00, 0x00, 0x09, 0x07, 0x16, 0x02, 0x05, 0x00, 0x02, 0x02, 0x12, 0x00, 0x00, 0x04, 0x0F, 0x0F, 0x00, 0x04, 0x00, 0x02, 0x02, 0x03, 0x00]
    },
    { 
      name: "Chakan - Intro Pad 2", 
      description: "Mysterious, haunting atmospheric layer.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x00, 0x02, 0x00, 0x03, 0x00, 0x01, 0x01, 0x00, 0x04, 0x00, 0x01, 0x06, 0x00, 0x00, 0x03, 0x2D, 0x06, 0x01, 0x04, 0x05, 0x00, 0x01, 0x06, 0x00, 0x00, 0x01, 0x19, 0x05, 0x05, 0x02, 0x04, 0x00, 0x02, 0x05, 0x03, 0x00, 0x01, 0x0C, 0x04, 0x15, 0x00, 0x06, 0x00, 0x01, 0x04, 0x02, 0x00]
    },
    { 
      name: "Chakan - Intro Pad 3", 
      description: "Dark, brooding alchemical texture.",
      data: [0x0B, 0x02, 0x01, 0x00, 0x04, 0x04, 0x00, 0x00, 0x00, 0x13, 0x05, 0x0B, 0x0A, 0x00, 0x00, 0x03, 0x12, 0x00, 0x05, 0x00, 0x05, 0x06, 0x09, 0x08, 0x00, 0x00, 0x05, 0x18, 0x00, 0x01, 0x0C, 0x04, 0x03, 0x0C, 0x08, 0x00, 0x00, 0x03, 0x13, 0x00, 0x0C, 0x0C, 0x07, 0x08, 0x06, 0x0B, 0x00, 0x00, 0x05, 0x08, 0x00]
    }
];
